import React, { useState, useMemo } from 'react';
import DataTable from './DataTable';
import MetricCard from './MetricCard';
import ChartVisualization from './ChartVisualization';
import './ResultsDisplay.css';

const ResultsDisplay = ({ results, onClear }) => {
  const [showSQL, setShowSQL] = useState(false);
  const [selectedVisualization, setSelectedVisualization] = useState('table');

  // Determine the type of results and format accordingly
  const resultType = useMemo(() => {
    if (!results || !results.results || results.results.length === 0) {
      return 'empty';
    }

    const data = results.results;
    
    // Check if it's a single metric (one row, one column with numeric value)
    if (data.length === 1 && Object.keys(data[0]).length === 1) {
      const value = Object.values(data[0])[0];
      if (typeof value === 'number' || !isNaN(Number(value))) {
        return 'metric';
      }
    }

    // Check if it's a simple count/total
    if (data.length === 1 && Object.keys(data[0]).length <= 3) {
      return 'summary';
    }

    // Otherwise it's tabular data
    return 'table';
  }, [results]);

  // Format AI response for display
  const formatAIResponse = (response) => {
    if (!response) return '';
    
    // Clean up the response text
    return response
      .replace(/^\"|\"$/g, '') // Remove quotes at start/end
      .replace(/\n/g, ' ') // Replace newlines with spaces
      .trim();
  };

  // Export data to CSV
  const exportToCSV = () => {
    if (!results || !results.results) return;

    const data = results.results;
    const headers = Object.keys(data[0] || {});
    
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Escape quotes and wrap in quotes if contains comma
          return typeof value === 'string' && value.includes(',') 
            ? `"${value.replace(/"/g, '""')}"` 
            : value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `got-tea-analytics-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Copy data to clipboard
  const copyToClipboard = async () => {
    if (!results || !results.results) return;

    try {
      const data = results.results;
      const text = JSON.stringify(data, null, 2);
      await navigator.clipboard.writeText(text);
      
      // Show temporary feedback (you could implement a toast notification)
      console.log('Data copied to clipboard');
    } catch (err) {
      console.error('Failed to copy data:', err);
    }
  };

  if (!results) {
    return null;
  }

  return (
    <div className="results-display">
      <div className="results-container">
        {/* Results Header */}
        <div className="results-header">
          <div className="results-title">
            <span className="results-icon">🎯</span>
            <h3>Analysis Results</h3>
          </div>
          <div className="results-actions">
            <button 
              className="action-button secondary"
              onClick={() => setShowSQL(!showSQL)}
              title="View generated SQL"
            >
              <span>🔍</span>
              SQL
            </button>
            <button 
              className="action-button secondary"
              onClick={copyToClipboard}
              title="Copy data"
            >
              <span>📋</span>
              Copy
            </button>
            <button 
              className="action-button secondary"
              onClick={exportToCSV}
              title="Export as CSV"
            >
              <span>📊</span>
              Export
            </button>
            <button 
              className="action-button danger"
              onClick={onClear}
              title="Clear results"
            >
              <span>✕</span>
              Clear
            </button>
          </div>
        </div>

        {/* AI Response */}
        {results.aiResponse && (
          <div className="ai-response">
            <div className="ai-avatar">💬</div>
            <div className="ai-message">
              {formatAIResponse(results.aiResponse)}
            </div>
          </div>
        )}

        {/* Results Content */}
        <div className="results-content">
          {resultType === 'empty' && (
            <div className="empty-results">
              <div className="empty-icon">🔍</div>
              <h4>No matching data found</h4>
              <p>Try adjusting your query or using different keywords.</p>
            </div>
          )}

          {resultType === 'metric' && (
            <MetricCard 
              data={results.results[0]}
              prompt={results.prompt}
            />
          )}

          {resultType === 'summary' && (
            <div className="summary-results">
              {Object.entries(results.results[0]).map(([key, value]) => (
                <div key={key} className="summary-item">
                  <div className="summary-label">{key}</div>
                  <div className="summary-value">{value}</div>
                </div>
              ))}
            </div>
          )}

          {resultType === 'table' && (
            <div className="table-results">
              <div className="table-header">
                <div className="table-info">
                  <span className="row-count">
                    {results.rowCount} {results.rowCount === 1 ? 'result' : 'results'}
                  </span>
                </div>
                <div className="view-toggles">
                  <button
                    className={`toggle-button ${selectedVisualization === 'table' ? 'active' : ''}`}
                    onClick={() => setSelectedVisualization('table')}
                  >
                    📋 Table
                  </button>
                  <button
                    className={`toggle-button ${selectedVisualization === 'chart' ? 'active' : ''}`}
                    onClick={() => setSelectedVisualization('chart')}
                  >
                    📊 Chart
                  </button>
                </div>
              </div>

              {selectedVisualization === 'table' ? (
                <DataTable data={results.results} />
              ) : (
                <ChartVisualization 
                  data={results.results} 
                  prompt={results.prompt}
                />
              )}
            </div>
          )}
        </div>

        {/* SQL Query Display */}
        {showSQL && results.generatedSQL && (
          <div className="sql-display">
            <div className="sql-header">
              <h4>Generated SQL Query</h4>
              <button 
                className="close-sql"
                onClick={() => setShowSQL(false)}
              >
                ✕
              </button>
            </div>
            <pre className="sql-code">
              <code>{results.generatedSQL}</code>
            </pre>
          </div>
        )}

        {/* Results Footer */}
        <div className="results-footer">
          <div className="execution-info">
            <span className="execution-time">
              Query executed at {new Date().toLocaleTimeString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsDisplay; 